import React from 'react';
import {Store} from '../../models/store.model';
import AppTable, {TableColumn} from '../../../../Presentation/components/ui/AppTable';
import AppTableHeaderCell from '../../../../Presentation/components/ui/AppTableHeaderCell';
import {AppText} from '../../../../Presentation/components/ui/Typography';
import {BaseTableProps} from '../../../../types/table.types';
import {StorePaymentStatusBadge} from '../common/StorePaymentStatusBadge';
import {StoreTableActions} from './StoreTableActions';
import {useTableExport, useExportConfig} from '../../../../Presentation/hooks/useTableExport';
import ExportButton from '../../../../Presentation/components/ui/ExportButton';
import {ExportFormat} from '../../../../types/export.types';


// صورة المتجر (profileImageUrl) + اسم المتجر + اسم المدير + اسم مطور التسويق

const PLACEHOLDER_IMG = '/assets/images/store_placeholder.png';

type Props = BaseTableProps<Store> & {
  onDeleteStore?: (id: string) => void;
  deletingStoreId?: string | null;
  onViewDetails?: (store: Store) => void;
  exportable?: boolean;
  loading?: boolean;
};

const StoreTable: React.FC<Props> = ({
  data: stores,
  page,
  pageSize,
  totalCount,
  onPageChange,
  onPageSizeChange,
  onDeleteStore,
  deletingStoreId,
  onViewDetails,
  showPageSizeSelector = true,
  showPaginationInfo = true,
  exportable = false,
  loading = false
}) => {
  // Export configuration
  const {createConfig} = useExportConfig<Store>();

  const exportConfig = createConfig([
    {key: 'name', title: 'اسم المتجر'},
    {key: 'phoneNumber', title: 'رقم الهاتف'},
    {key: 'managerName', title: 'مدير المتجر'},
    {key: 'developerName', title: 'مطور التسويق'},
    {key: 'address', title: 'العنوان'},
    {
      key: 'paymentStatus',
      title: 'حالة الدفع',
      formatter: (value) => {
        switch (value) {
          case 'paid': return 'مدفوعة';
          case 'pending': return 'في الانتظار';
          case 'overdue': return 'متأخرة';
          default: return String(value);
        }
      }
    }
  ], 'متاجر', 'تصدير بيانات المتاجر', 'تصدير جميع بيانات المتاجر مع تفاصيلها');

  // Export hook
  const {exportData, isExporting, canExport, recordCount} = useTableExport({
    data: stores,
    config: exportConfig
  });

  // Handle export
  const handleExport = async (format: ExportFormat) => {
    await exportData(format);
  };

  const columns: TableColumn<Store>[] = [
    {
      key: 'profileImageUrl',
      title: <AppTableHeaderCell label="الصورة" />,
      render: (store: Store) => (
        <img
          src={store.profileImageUrl || PLACEHOLDER_IMG}
          alt={store.name}
          className="w-10 h-10 rounded-full object-cover mx-auto"
        />
      ),
      className: 'w-16'
    },
    {
      key: 'name',
      title: <AppTableHeaderCell label="اسم المتجر" />,
      render: (store: Store) => <AppText>{store.name}</AppText>
    },
    {
      key: 'phoneNumber',
      title: <AppTableHeaderCell label="رقم الهاتف" />,
      render: (store: Store) => <AppText>{store.phoneNumber}</AppText>
    },
    {
      key: 'managerName',
      title: <AppTableHeaderCell label="مدير المتجر" />,
      render: (store: Store) => <AppText>{store.managerName}</AppText>
    },
    {
      key: 'developerName',
      title: <AppTableHeaderCell label="مطور التسويق" />,
      render: (store: Store) => <AppText>{store.developerName}</AppText>
    },
    {
      key: 'address',
      title: <AppTableHeaderCell label="العنوان" />,
      render: (store: Store) => <AppText>{store.address}</AppText>
    },
    {
      key: 'paymentStatus',
      title: <AppTableHeaderCell label="حالة الدفع" />,
      render: (store: Store) => (
        <StorePaymentStatusBadge status={store.paymentStatus} />
      )
    }
  ];

  return (
    <div className="flex-1 min-h-[calc(100vh-80px)] flex flex-col">
      {/* Export Button */}
      {exportable && (
        <div className="mb-4 flex justify-end">
          <ExportButton
            onExport={handleExport}
            isExporting={isExporting}
            canExport={canExport}
            recordCount={recordCount}
            showDropdown={true}
          />
        </div>
      )}

      <div className="overflow-x-auto">
        <AppTable
          columns={columns}
          data={stores}
          rowKey={(row, index) => `store-${row.id}-${index || 0}`}
          emptyText="لا توجد متاجر"
          page={page}
          pageSize={pageSize}
          totalCount={totalCount}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          showPageSizeSelector={showPageSizeSelector}
          showPaginationInfo={showPaginationInfo}
          loading={loading}
          loadingRows={pageSize || 10}
          loadingMessage="جاري تحميل المتاجر..."
          actions={(store) => StoreTableActions({
            store,
            deletingStoreId,
            onViewDetails,
            onDeleteStore
          })}
        />
      </div>
    </div>
  );
};

export default StoreTable;
